<template>
    <st-section
        :header="$t('REAL_ESTATE.LIST_HEADER')"
        customClass="list-section"
        id="real-estate-target"
        :validate="!viewMode"
        :wasValidated="wasValidated"
        :validation="validationStatus">
        <template #body>
            <carousel-3d
                :controls-visible="true"
                :width="400"
                :height="220"
                :space="300"
                :count="itemsComputed.length"
                dispay="3"
                ref="realEstateCarousel">
                <slide v-for="(item, i) in itemsComputed" :index="i" :key="i">
                    <div class="carousel-card real-estate-card">
                        <real-estate-item-card
                            v-if="!item.isPlaceholder"
                            @edit="onEdit"
                            @delete="onDelete"
                            :item="item"
                            :index="i"
                            :viewMode="viewMode"
                        ></real-estate-item-card>
                        <div class="card-user-placeholder" v-else>
                            <i class="fas fa-home"></i>
                        </div>
                    </div>
                </slide>
            </carousel-3d>
            <div class="d-flex list-footer">
                <div class="col-9 d-flex align-items-center px-0">
                    <span>{{ total }}</span>
                </div>
                <div class="col-3 d-flex justify-content-end px-0" v-if="!viewMode">
                    <st-button :callback="openRealEstateModal" variant="link">
                        <span class="d-flex align-items-center">
                            <i class="fas fa-plus"></i>
                            {{ $t("REAL_ESTATE.FORM.ADD_BTN") }}
                        </span>
                    </st-button>
                </div>
            </div>

            <real-estate-modal
                ref="realEstateModal"
                :item="selectedEntity"
                :cadastralNumber="cadastralNumber"
                @processRealEstate="processRealEstate"
                @cancel="cancel"
            ></real-estate-modal>

            <real-estate-view-modal
                ref="realEstateViewModal"
                :items="items"
                :index="selectedEntityIndex"
            ></real-estate-view-modal>

        </template>
    </st-section>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from "vuex";
import { Carousel3d, Slide } from 'vue-carousel-3d';
import { RealEstateModel } from "@/modules/applications/models/real-estate-model";
import  RealEstateModal from "@/modules/applications/components/real-estate/RealEstateModal";
import  RealEstateViewModal from "@/modules/applications/components/real-estate/RealEstateViewModal";
import  RealEstateItemCard from "@/modules/applications/components/real-estate/RealEstateItemCard";

const { fields } = RealEstateModel;

export default {
    name: "RealEstateList",
    components: {
        RealEstateModal,
        RealEstateViewModal,
        RealEstateItemCard,
        Carousel3d,
        Slide
    },
    props: {
        viewMode: {
            type: Boolean,
            default: false
        },
        applicationType: {
            type: String
        },
        wasValidated: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            items: [],
            selectedEntity: null,
            selectedEntityIndex: null,
            cadastralNumber: [],
        };
    },
    computed: {
        ...mapGetters({
            record: "applications/form/record",
        }),
        fields() {
            fields.cadastral_number.tdClass = "d-flex col-md-2 align-items-center";
            fields.address.tdClass = "d-flex col-md-3 align-items-center";
            fields.real_estate_type.tdClass = "d-flex col-md-5 align-items-center";
            return [
                fields.cadastral_number,
                fields.address,
                fields.real_estate_type,
            ];
        },
        total() {
            return this.items.length === 1
                ? this.$t("REAL_ESTATE.SINGLE", { total: 1 })
                : this.$t("REAL_ESTATE.MULTIPLE", { total: this.items.length });
        },
        itemsComputed() {
            const countItems = this.items.length;
            if (!countItems >= 3) return [...this.items];
            const previewItems = 3;
            let visibleItems = [];
            if (countItems > 0) {
                visibleItems = [...this.items];
            }
            const placeholderItems = previewItems - countItems;
            for(let i = 0; i < placeholderItems; i+=1) {
                visibleItems.push({
                    isPlaceholder: true,
                });
            }

            return visibleItems;
        },
        validationStatus() {
            return this.items.length ? 'success' : 'error';
        }
    },
    methods: {
        ...mapActions({
            doSetRealEstates: "applications/form/doSetRealEstates",
        }),
        openRealEstateModal() {
            this.selectedEntity = null;
            this.selectedEntityIndex = null;
            this.$refs.realEstateModal.show();
        },
        async processRealEstate(data) {
            const isEdit = Object.keys(data).some((el) => el === 'index');
            if (isEdit) {
                Vue.set(this.items, data.index, data.realEstateDetails);
            } else {
                this.items.push({
                    ...data.realEstateDetails,
                    index: this.items.length
                });
                this.goToSlide(this.items.length - 1);
            }

            this.$emit('changeList', this.items, 'real_estate_target');

            this.selectedEntity = null;
            this.selectedEntityIndex = null;
            this.refreshCadastralNumber();
            this.doSetRealEstates(this.items);
        },
        onEdit({item, index}) {
            this.selectedEntity = item;
            this.selectedEntity.index = index;
            this.selectedIndex = index;
            this.selectedEntity.identification_files = this.items.identification_files;
            this.$refs.realEstateModal.show();
        },
        onDelete({index}){
            this.items.splice(index, 1);
            this.refreshCadastralNumber();
            this.goToSlide(0);
            this.doSetRealEstates(this.items);
        },
        cancel() {
            this.selectedEntity = null;
            this.selectedEntityIndex = null;
        },
        refreshCadastralNumber() {
            this.cadastralNumber = this.items.map((el, index) => {
                return {
                    cadastral_number: el.cadastral_number,
                    territorial_administrative_unit: el.territorial_administrative_unit,
                    index: index
                }
            });
        },
        goToSlide(index) {
            this.$refs.realEstateCarousel.goSlide(index);
        },
    },
    created() {
        this.items = [];

        if (this.$route.params.id ) {
            this.items = this.record ? this.record.form.real_estate_target : [];
            this.items.identification_files = this.record ? this.record.identification_files : [];
            this.cadastralNumber = this.record ? this.record.form.real_estate_target.map((el, index) => {
                return {
                    cadastral_number: el.cadastral_number,
                    index: index
                }
            }) : [];
        }
    },
};
</script>
